import React, { useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Save, GraduationCap, CalendarPlus } from 'lucide-react';
import { formatApiError } from '@/lib/errors';

const AVAILABILITY_OPTIONS = [
  { value: 'free', label: 'Plätze frei' },
  { value: 'few', label: 'Wenige Plätze' },
  { value: 'full', label: 'Belegt / Warteliste' },
];

export const TeachingConfigEditor = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [options, setOptions] = useState({});
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    api.get(`/tenants/${tenantId}/registration-settings`)
      .then((r) => {
        setCategories(r.data.instrument_categories || []);
        setOptions(r.data.subject_teaching_options || {});
        setOffers(r.data.registration_course_offers || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenantId]);

  const save = async () => {
    if (!tenantId) {
      toast.error('Kein Mandant ausgewählt — bitte oben links den Mandanten wählen.');
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/registration-settings`, {
        subject_teaching_options: options,
        registration_course_offers: offers,
      });
      setOptions(r.data.subject_teaching_options || {});
      setOffers(r.data.registration_course_offers || []);
      toast.success('Unterrichts-Konfiguration gespeichert');
    } catch (e) {
      toast.error(formatApiError(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const instrumentSubjects = (categories[0]?.items) || [];
  const courseSubjects = categories.slice(1).flatMap((c) => c.items || []);

  const optName = (o) => (typeof o === 'string' ? o : (o?.name || ''));
  const optPrice = (o) => (typeof o === 'string' ? '' : (o?.price_text || ''));

  const setSubjectOptions = (subject, text) => {
    setOptions((p) => {
      const prevPrices = Object.fromEntries((p[subject] || []).map((o) => [optName(o), optPrice(o)]));
      const names = text.split(',').map((o) => o.trim()).filter(Boolean);
      return { ...p, [subject]: names.map((n) => ({ name: n, price_text: prevPrices[n] || '' })) };
    });
  };

  const distinctOptionNames = [...new Set(
    Object.values(options).flatMap((opts) => (opts || []).map(optName)).filter(Boolean),
  )];
  const priceFor = (name) => {
    for (const opts of Object.values(options)) {
      const hit = (opts || []).find((o) => optName(o) === name && optPrice(o));
      if (hit) return optPrice(hit);
    }
    return '';
  };
  const setPriceForAll = (name, price) => {
    setOptions((p) => Object.fromEntries(Object.entries(p).map(([subj, opts]) => ([
      subj,
      (opts || []).map((o) => (optName(o) === name ? { name, price_text: price } : (typeof o === 'string' ? { name: o, price_text: '' } : o))),
    ]))));
  };

  const addOffer = () => setOffers((p) => ([
    ...p,
    {
      id: `offer_${Math.random().toString(36).slice(2, 12)}`,
      subject: courseSubjects[0] || '',
      title: '',
      schedule_text: '',
      location_text: '',
      teacher_name: '',
      description: '',
      target_group: '',
      period_text: '',
      cost_text: '',
      remarks: '',
      availability: 'free',
    },
  ]));
  const updateOffer = (idx, patch) => setOffers((p) => p.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  const removeOffer = (idx) => setOffers((p) => p.filter((_, i) => i !== idx));

  if (loading) return <div className="text-sm text-slate-500 py-4">Unterrichts-Konfiguration wird geladen …</div>;

  return (
    <div data-testid="teaching-config-editor">
      {/* Unterrichtsarten je Fach */}
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
          <GraduationCap className="w-4 h-4 text-sky-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">Unterrichtsarten je Fach</h3>
          <p className="text-sm text-slate-500">
            Für Instrumental-/Vokalfächer (Kategorie 1). Optionen durch Komma trennen — enthält eine Option „Gruppen…",
            greift automatisch der Gruppenunterricht-Flow.
          </p>
        </div>
      </div>

      <div className="mt-4 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-96 overflow-y-auto">
        {instrumentSubjects.length === 0 ? (
          <div className="p-3 text-sm text-slate-500">Keine Instrumentalfächer in Kategorie 1 gepflegt.</div>
        ) : instrumentSubjects.map((subj) => (
          <div key={subj} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`teaching-options-row-${subj}`}>
            <div className="w-44 shrink-0 text-sm font-medium text-slate-800">{subj}</div>
            <Input
              className="flex-1 min-w-[240px] h-8 text-sm"
              value={(options[subj] || []).map(optName).join(', ')}
              onChange={(e) => setSubjectOptions(subj, e.target.value)}
              placeholder="z. B. 4er Karte, Einzelunterricht 30 Min, Gruppenunterricht (2 bis 4er)"
              data-testid={`teaching-options-input-${subj}`}
            />
          </div>
        ))}
      </div>

      {/* Preise je Unterrichtsart */}
      {distinctOptionNames.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-slate-800 mb-1">Preise je Unterrichtsart</h4>
          <p className="text-xs text-slate-500 mb-2">
            Wird im Formular blass hinter der Option angezeigt (z. B. „ab 83,00 € mtl."). Gilt für alle Fächer mit dieser Unterrichtsart. Leer = keine Preisanzeige.
          </p>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {distinctOptionNames.map((name) => (
              <div key={name} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`teaching-price-row-${name}`}>
                <div className="w-64 shrink-0 text-sm text-slate-800">{name}</div>
                <Input
                  className="flex-1 min-w-[180px] h-8 text-sm"
                  value={priceFor(name)}
                  onChange={(e) => setPriceForAll(name, e.target.value)}
                  placeholder="z. B. ab 83,00 € mtl."
                  data-testid={`teaching-price-input-${name}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manuelle Kursangebote */}
      <div className="flex items-center justify-between mt-8 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
            <CalendarPlus className="w-4 h-4 text-brand-red" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Zusätzliche Kursangebote</h3>
            <p className="text-sm text-slate-500">
              Nur für das Anmeldeformular gepflegte Angebote — echte Kurse aus dem System werden automatisch gefunden.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={addOffer} data-testid="teaching-add-offer-btn">
          <Plus className="h-4 w-4 mr-1" /> Angebot
        </Button>
      </div>

      {offers.length === 0 ? (
        <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg mt-3">
          Keine manuellen Angebote. Kursangebote werden aus den aktiven Kursen des Systems ermittelt.
        </div>
      ) : (
        <div className="space-y-3 mt-3">
          {offers.map((o, idx) => (
            <div key={o.id} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`teaching-offer-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Fach</Label>
                  <Select value={o.subject} onValueChange={(v) => updateOffer(idx, { subject: v })}>
                    <SelectTrigger className="h-8 text-sm" data-testid={`teaching-offer-${idx}-subject`}><SelectValue placeholder="Fach" /></SelectTrigger>
                    <SelectContent>
                      {courseSubjects.map((sub) => <SelectItem key={sub} value={sub}>{sub}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">Titel *</Label>
                  <Input className="h-8 text-sm" value={o.title} onChange={(e) => updateOffer(idx, { title: e.target.value })} placeholder="z. B. Hip-Hop Tanz-AG - Beat Breakers" data-testid={`teaching-offer-${idx}-title`} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Termin</Label>
                  <Input className="h-8 text-sm" value={o.schedule_text} onChange={(e) => updateOffer(idx, { schedule_text: e.target.value })} placeholder="Dienstag, 15:00 - 16:00 Uhr" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ort / Raum</Label>
                  <Input className="h-8 text-sm" value={o.location_text} onChange={(e) => updateOffer(idx, { location_text: e.target.value })} placeholder="Musik Akademie — Raum Studio 7" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Dozent:in</Label>
                  <Input className="h-8 text-sm" value={o.teacher_name} onChange={(e) => updateOffer(idx, { teacher_name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Zielgruppe</Label>
                  <Input className="h-8 text-sm" value={o.target_group} onChange={(e) => updateOffer(idx, { target_group: e.target.value })} placeholder="Für Kinder von 4 bis 6 Jahren" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Zeitraum</Label>
                  <Input className="h-8 text-sm" value={o.period_text} onChange={(e) => updateOffer(idx, { period_text: e.target.value })} placeholder="01.09.2026 bis 31.07.2027" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Kosten</Label>
                  <Input className="h-8 text-sm" value={o.cost_text} onChange={(e) => updateOffer(idx, { cost_text: e.target.value })} placeholder="33,00 Euro monatlich" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Beschreibung</Label>
                <Textarea rows={2} className="text-sm" value={o.description} onChange={(e) => updateOffer(idx, { description: e.target.value })} />
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs text-slate-500">Bemerkungen</Label>
                  <Input className="h-8 text-sm" value={o.remarks} onChange={(e) => updateOffer(idx, { remarks: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Auslastung</Label>
                  <Select value={o.availability} onValueChange={(v) => updateOffer(idx, { availability: v })}>
                    <SelectTrigger className="h-8 text-sm w-44" data-testid={`teaching-offer-${idx}-availability`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AVAILABILITY_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" size="icon" className="text-rose-500 hover:text-rose-700 h-8 w-8" onClick={() => removeOffer(idx)} data-testid={`teaching-offer-${idx}-delete`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={saving} data-testid="teaching-config-save-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Speichern…' : 'Unterrichts-Konfiguration speichern'}
        </Button>
      </div>
    </div>
  );
};
